import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  Location,
  LocationCreate,
  LocationPersonnelWithStudy,
  LocationUpdate,
} from "../types";

const SITE_LOCATIONS_KEY = (siteId: string) => ["sites", siteId, "locations"] as const;
const LOCATION_KEY = (id: string) => ["locations", id] as const;

export function useSiteLocations(siteId: string | undefined) {
  return useQuery<Location[]>({
    queryKey: siteId ? SITE_LOCATIONS_KEY(siteId) : ["sites", "_", "locations"],
    queryFn: () => apiRequest(`/api/sites/${siteId}/locations`),
    enabled: Boolean(siteId),
  });
}

export function useLocation(id: string | undefined) {
  return useQuery<Location>({
    queryKey: id ? LOCATION_KEY(id) : ["locations", "_"],
    queryFn: () => apiRequest(`/api/locations/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateLocation(siteId: string) {
  const qc = useQueryClient();
  return useMutation<Location, Error, LocationCreate>({
    mutationFn: (payload) =>
      apiRequest(`/api/sites/${siteId}/locations`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SITE_LOCATIONS_KEY(siteId) }),
  });
}

export function useUpdateLocation(id: string, siteId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<Location, Error, LocationUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/locations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (loc) => {
      qc.setQueryData(LOCATION_KEY(id), loc);
      if (siteId) qc.invalidateQueries({ queryKey: SITE_LOCATIONS_KEY(siteId) });
    },
  });
}

// Aggregated personnel across every Participation under this Location
// — used by the Location expanded panel's Personnel section. The
// rows carry a study_name so the UI can show which participation each
// person belongs to. Deduplicated server-side: one row per (person ×
// participation), keeping the latest title.
export function useLocationPersonnel(
  locationId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<LocationPersonnelWithStudy[]>({
    queryKey: ["locations", locationId, "personnel"],
    queryFn: () => apiRequest(`/api/locations/${locationId}/personnel`),
    enabled: Boolean(locationId) && (opts.enabled ?? true),
  });
}

// Full history for one person at a location — every LocationPersonnel
// row written by every delegation-log version sorted newest-first.
// Backs the "click the name to see role/title changes" drawer.
export function useLocationPersonnelHistory(
  locationId: string | undefined,
  firstName: string,
  lastName: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<LocationPersonnelWithStudy[]>({
    queryKey: ["locations", locationId, "personnel", "history", firstName, lastName],
    queryFn: () => {
      const params = new URLSearchParams({
        first_name: firstName,
        last_name: lastName,
      });
      return apiRequest(
        `/api/locations/${locationId}/personnel/history?${params.toString()}`,
      );
    },
    enabled:
      Boolean(locationId) &&
      Boolean(lastName) &&
      (opts.enabled ?? true),
  });
}

export function useDeleteLocation(siteId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiRequest(`/api/locations/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SITE_LOCATIONS_KEY(siteId) }),
  });
}
